import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { TenantContextService } from '../../core/tenant-context.service';
import { DEMO_ORG_ID } from './demo-sandbox.config';

/**
 * A persistent, unmissable banner shown whenever the signed-in session
 * belongs to the demo tenant. Driven off the real `orgId` from the auth
 * claims rather than a local flag, so it cannot get out of sync with which
 * data the user is actually looking at.
 *
 * The point is safety-of-interpretation, not decoration: without it,
 * screenshots of the demo are indistinguishable from screenshots of a live
 * clinical system, and fictional wound/financial data could be mistaken
 * for a real patient's.
 */
@Component({
  selector: 'app-demo-mode-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="demo-banner" *ngIf="isDemo$ | async">
      <strong>DEMO SANDBOX</strong>
      <span>Fictional data for evaluation only — not a real patient record.</span>
    </div>
  `,
  styles: [`
    .demo-banner {
      position: sticky;
      top: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 6px 12px;
      background: repeating-linear-gradient(
        45deg, #b45309, #b45309 12px, #92400e 12px, #92400e 24px
      );
      color: #fff;
      font-size: 12.5px;
      letter-spacing: 0.02em;
      text-align: center;
    }
    .demo-banner strong {
      letter-spacing: 0.09em;
    }
    @media (max-width: 560px) {
      .demo-banner span { display: none; }
    }
  `],
})
export class DemoModeBannerComponent {
  private readonly tenantContext = inject(TenantContextService);

  readonly isDemo$: Observable<boolean> = this.tenantContext.context$.pipe(
    map((context) => context.orgId === DEMO_ORG_ID),
    catchError(() => of(false))
  );
}
